import { onMounted, onUnmounted, watch } from 'vue'

import { useUtil } from '@/composables/util'
import { KIPPAH_REMINDER_TIMEOUT_MS, KIPPAH_SVG, LAST_ACTIVITY_KEY, SESSION_INACTIVITY_THRESHOLD_MS } from '@/constants/practiceConstants'
import UpdateKippahReminder from '@/mutations/UpdateKippahReminder.graphql'
import store from '@/store'
import { runReportedMutation } from '@/store/mutationHelpers'

function isNewSession(): boolean {
  const last = localStorage.getItem(LAST_ACTIVITY_KEY)
  if (!last) {
    return true
  }
  return Date.now() - parseInt(last, 10) > SESSION_INACTIVITY_THRESHOLD_MS
}

function updateActivityTimestamp(): void {
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))
}

/**
 * Shows a kippah reminder snackbar when a student starts a new session
 * (after login or after a period of inactivity). Listens for visibilitychange
 * events to detect tab-return-after-inactivity.
 *
 * Call from PracticeView's setup. Requires barMitzvah data to be available in the store.
 */
export function useKippahReminder(): void {
  const { isStudent } = useUtil()

  function disableReminder(): void {
    runReportedMutation(UpdateKippahReminder, { kippahReminder: false }, 'updateKippahReminder', store.dispatch.snackbar.showError).then(
      (payload) => {
        if (payload) {
          store.dispatch.student.fetchBarMitzvah()
        }
      },
    )
  }

  function checkAndRemind(): void {
    if (!isStudent()) {
      return
    }

    if (!store.getters.student.barMitzvahKippahReminder) {
      return
    }

    if (!isNewSession()) {
      updateActivityTimestamp()
      return
    }

    store.dispatch.snackbar.add({
      action: {
        handler: disableReminder,
        label: "Don't remind me",
      },
      iconSvg: KIPPAH_SVG,
      message: 'Remember to put on your kippah!',
      state: 'info',
      timeout: KIPPAH_REMINDER_TIMEOUT_MS,
    })

    updateActivityTimestamp()
  }

  function onVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      checkAndRemind()
    }
  }

  onMounted(() => {
    // Wait for barMitzvah data to load before checking
    const stopWatch = watch(
      () => store.getters.student.barMitzvah,
      (barMitzvah) => {
        if (barMitzvah) {
          checkAndRemind()
          stopWatch()
        }
      },
      { immediate: true },
    )

    document.addEventListener('visibilitychange', onVisibilityChange)
  })

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', onVisibilityChange)
  })
}
